import { LoadingButton } from "@mui/lab";
import { Grid } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import RkdCard from "../../app/component/card/RkdCard";
import { RBDItemType, RBDType } from "../../app/config/enum";
import { useStore } from "../../app/stores/store";
import RBDSettingAction from "./RBDSettingAction";
import RBDSettingEquipment from "./RBDSettingEquipment";
import RBDSettingItemType from "./RBDSettingItemType";
import RBDSettingStation from "./RBDSettingStation";
import RBDSettingSystem from "./RBDSettingSystem";
import RBDSettingTable from "./RBDSettingTable";
import RBDSettingTransmissionElement from "./RBDSettingTransmissionElement";
import RBDSettingTransmissionType from "./RBDSettingTransmissionType";
import useHeight from "../../app/hook/useHeight";
import RBDSettingDiagram from "./RBDSettingDiagram";

function RBDSetting() {
    const { commonStore, rbdStore, snackbarStore } = useStore();
    const { card: c, etc: e, other: o } = useStore().assetStore.elementsHeight
    const { setOtherHeight, setCardHeight, setEtcHeight } = useStore().assetStore
    const { open } = useStore().dialogStore
    const { ref: otherRef, height: otherHeight } = useHeight()
    const { ref: cardRef, height: cardHeight } = useHeight()
    const navigate = useNavigate();

    // Set Title
    useEffect(() => {
        commonStore.setTitlePage("Reliability Block Diagram (RBD) - Setting");
    }, [commonStore]);

    // Get RBD Type (system / equipment)
    const match = useMatch("rbd/setting/:rbdType/:id");
    useEffect(() => {
        rbdStore.resetStates();
        rbdStore.setSelectedRbdType(match?.params.rbdType === "system" ? RBDType.SYSTEM : RBDType.STATION);
        rbdStore.setSelectedId(parseInt(match?.params.id ?? "0"));
        rbdStore.setSelectedStation(commonStore.selectedStation);
    }, [rbdStore, match?.params.id, match?.params.rbdType, commonStore.selectedStation]);

    const handleSave = async () => {
        if (!commonStore.selectedStation) return snackbarStore.show("error", "Station has not been selected");
        if (!rbdStore.selectedRbdType) return snackbarStore.show("error", "RBD type unknown");
        if (!match?.params.id) return snackbarStore.show("error", "ID is unknown");

        await rbdStore.saveRbd(rbdStore.selectedRbdType, parseInt(match.params.id)).then(() => {
            navigate('/rbd');
            snackbarStore.show("success", "RBD saved successfully");
        })
    }

    const handleSaveDialog = () => open({
        action: handleSave,
        actionText: 'Save',
        description: 'Are you sure you want to save data ?',
        closeText: 'Cancel',
        title: 'Save RBD'
    })

    // Setup diagram height
    useEffect(() => {
        setOtherHeight(otherHeight)
    }, [otherHeight, setOtherHeight])
    useEffect(() => {
        setCardHeight(cardHeight)
    }, [cardHeight, setCardHeight])
    useEffect(() => {
        setEtcHeight(150)
        return () => setEtcHeight(60)
    }, [setEtcHeight])

    return (
        <RkdCard ref={cardRef}>
            <Grid ref={otherRef} container spacing={2}>
                <Grid item xs={5}>
                    <RBDSettingItemType  />   
                    {rbdStore.selectedItemType === RBDItemType.ELEMENT &&
                        <>
                            <RBDSettingStation />
                            <RBDSettingSystem disabled={rbdStore.selectedRbdType === RBDType.SYSTEM} />
                            {rbdStore.selectedRbdType === RBDType.SYSTEM &&
                                <RBDSettingEquipment />
                            }
                        </>
                    }
                    {rbdStore.selectedItemType === RBDItemType.TRANSMISSION &&
                        <>
                            <RBDSettingTransmissionType />
                            <RBDSettingTransmissionElement />
                        </>
                    }
                    <RBDSettingAction />
                </Grid>
                <Grid item xs={7} style={{ height: 400 }}>
                    <RBDSettingTable />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <h3>Diagram</h3>
                    <RBDSettingDiagram height={Math.max(c - e - o, 250)}/>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <LoadingButton variant="contained" onClick={handleSaveDialog} loading={rbdStore.loadingSave} disabled={rbdStore.saveDisabled}>
                        Save
                    </LoadingButton>
                </Grid>
            </Grid>
        </RkdCard>
    );
}

export default observer(RBDSetting);